/**
 * Coach Schedule Hooks - TanStack Query hooks for coach schedule views
 *
 * Fetches class sessions filtered by instructor_id and date range
 * using the standard class_sessions API endpoint.
 */

import { useQuery } from "@tanstack/react-query"

import type { ClassSessionResponse } from "@/client/types.gen"
import { api } from "@/services/api"
import { useAuthStore } from "@/stores/auth"
import { unwrapStandardResponse } from "@/utils/apiResponse"

interface ClassSessionListData {
  items: ClassSessionResponse[]
  total: number
}

function formatDate(d: Date): string {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

function getDateRange(range: "week" | "upcoming"): {
  from: string
  to: string
} {
  const today = new Date()
  const from = formatDate(today)

  const endDate = new Date(today)
  if (range === "week") {
    endDate.setDate(endDate.getDate() + 6)
  } else {
    endDate.setDate(endDate.getDate() + 29)
  }
  const to = formatDate(endDate)

  return { from, to }
}

/**
 * Fetch class sessions for the logged-in coach within a date range.
 *
 * Uses the standard class_sessions endpoint with instructor_id filter
 * and session_date_from/session_date_to range.
 */
function useCoachClassSessions(range: "week" | "upcoming") {
  const user = useAuthStore((s) => s.user)
  const userId = user?.id

  const { from, to } = getDateRange(range)

  return useQuery({
    queryKey: ["coach", "schedule", range, userId, from, to],
    queryFn: async (): Promise<ClassSessionListData> => {
      if (!userId) {
        return { items: [], total: 0 }
      }

      const response =
        await api.classSessions.readClassSessionsApiV1ClassSessionsGet({
          instructorId: userId,
          sessionDateFrom: from,
          sessionDateTo: to,
          limit: 500,
          sortBy: "created_at",
          sortOrder: "asc",
        })

      const data = unwrapStandardResponse<ClassSessionListData>(response)
      return data ?? { items: [], total: 0 }
    },
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Fetch this week's class sessions for the coach (next 7 days).
 */
export function useCoachWeekSchedule() {
  return useCoachClassSessions("week")
}

/**
 * Fetch upcoming class sessions for the coach (next 30 days).
 */
export function useCoachUpcomingSchedule() {
  return useCoachClassSessions("upcoming")
}

/**
 * Group class sessions by date, sorted chronologically.
 *
 * Returns an array of { date, dateLabel, sessions } objects.
 */
export function groupSessionsByDate(
  sessions: ClassSessionResponse[],
): Array<{
  date: string
  dateLabel: string
  sessions: ClassSessionResponse[]
}> {
  const sorted = [...sessions].sort((a, b) => {
    const dateCmp = a.session_date.localeCompare(b.session_date)
    if (dateCmp !== 0) return dateCmp
    return a.session_start.localeCompare(b.session_start)
  })

  const groups = new Map<string, ClassSessionResponse[]>()
  for (const session of sorted) {
    const dateKey = session.session_date
    if (!groups.has(dateKey)) {
      groups.set(dateKey, [])
    }
    groups.get(dateKey)!.push(session)
  }

  return Array.from(groups.entries()).map(([dateKey, dateSessions]) => {
    const [year, month, day] = dateKey.split("-").map(Number)
    const dateObj = new Date(year, month - 1, day)
    const dateLabel = dateObj.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
    })

    return {
      date: dateKey,
      dateLabel,
      sessions: dateSessions,
    }
  })
}

/**
 * Format a session_start ISO string to a human-readable time like "4:00 PM".
 */
export function formatSessionTime(isoString: string): string {
  const date = new Date(isoString)
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
}
